import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { StatementListStyles } from '../../statements/sections/Statement.List';
import { StatementBalanceAction, useStatementBalanceProvider } from '../StatementBalance.Hooks';
import { useState } from 'react';
import { isEmpty } from 'lodash';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function StatementBalanceList() {
  const { lang } = getLocales();
  const [isLoadingSelectAll, setIsLoadingSelectAll] = useState<boolean>(undefined);
  const {
    listApi,
    pageRouter,
    selectedBalanceStatements,
    setSelectedBalanceStatements,
    exportStatementBalanceToPdf,
    getAllSelectedBalanceStatements,
  } = useStatementBalanceProvider();
  const { permissions } = useAuthStore();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.statementBalances}
      render={lang.noInfo}
    />
  ) : (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={
                selectedBalanceStatements?.length === listApi.payload?.count
                  ? 'fas-check-square'
                  : selectedBalanceStatements?.length === 0
                    ? 'far-square'
                    : 'fas-minus-square'
              }
              disabled={!listApi?.payload?.count || !permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST}
              onClick={async () => {
                setIsLoadingSelectAll(true);
                setSelectedBalanceStatements(await getAllSelectedBalanceStatements());
                setIsLoadingSelectAll(undefined);
              }}
              isLoading={isLoadingSelectAll}
              active={!isEmpty(selectedBalanceStatements) ? true : false}
              htmlElementProps={{ title: lang.selectAll }}
            />
          </th>
          <th>
            {lang.dealer}/{lang.contracts}
          </th>
          <th>#</th>
          <th>{lang.createdAt}</th>
          <th>{lang.representative}</th>
          <th>{lang.amount}</th>
          <th>{lang.tax}</th>
          <th>{lang.totalAmount}</th>
          <th data-actions>{lang.actions}</th>
        </tr>
      </thead>
      <tbody>
        <DataMap
          data={listApi.payload?.data}
          render={({ data: statementBalance }) => {
            const currentStatementBalance = selectedBalanceStatements?.find((it) => it.id === statementBalance.id);
            return (
              <tr key={statementBalance.id} style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                <td>
                  <Button
                    onClick={() => {
                      if (currentStatementBalance)
                        setSelectedBalanceStatements((prev) => prev?.filter((it) => it.id !== statementBalance.id));
                      else setSelectedBalanceStatements((prev) => [...(prev || []), statementBalance]);
                    }}
                    disabled={
                      !(
                        permissions.CREATE_STATEMENTBALANCE ||
                        permissions.EDIT_STATEMENTBALANCE ||
                        permissions.MODIFY_STATEMENTBALANCE_EMAIL
                      )
                    }
                    class={ThemeButtonCircle}
                    media={<Icon class={currentStatementBalance ? 'fas-check-square' : 'far-square'} />}
                    htmlElementProps={{ title: currentStatementBalance ? lang.unselect : lang.select }}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ ...StatementListStyles.info }}>{statementBalance.displayInfo?.info}</div>
                    <div style={{ ...StatementListStyles.title }}>{statementBalance.displayInfo?.title}</div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {statementBalance.isEmailSent && (
                        <Box
                          class={ThemeTagRounded}
                          style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                        >
                          <Icon class="fas-check" /> {lang.emailSent}
                        </Box>
                      )}
                    </div>
                  </div>
                </td>
                <td>{statementBalance.statementBalanceNumber}</td>
                <td>{localizeDate(statementBalance?.createdAtLocal, defaultDateTimeFormat)}</td>
                <td>{statementBalance.representative?.fullName || 'N/A'}</td>
                <td>
                  <b>
                    <FormatValue
                      value={`${(
                        statementBalance.contracts?.reduce((sum, obj) => sum + obj.paymentAmountCAD, 0) || 0
                      ).toFixed(2)}$`}
                    />
                  </b>
                </td>
                <td>
                  <b>
                    <FormatValue
                      value={`${(
                        statementBalance.contracts?.reduce((sum, obj) => sum + obj.paymentTaxCAD, 0) || 0
                      ).toFixed(2)}$`}
                    />
                  </b>
                </td>
                <td>
                  <b>
                    <FormatValue value={`${(statementBalance.totalAmountDueCAD || 0).toFixed(2)}$`} />
                  </b>
                </td>
                <td>
                  <Box class={ThemeWrapperStack}>
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statementBalance.id.toString(),
                        action: StatementBalanceAction.UPLOAD_DOCUMENT,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-upload" />}
                      htmlElementProps={{ title: lang.upload }}
                      disabled={!permissions.EDIT_STATEMENTBALANCE}
                      active
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statementBalance.id.toString(),
                        action: StatementBalanceAction.PRINT,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-print" />}
                      htmlElementProps={{ title: lang.print }}
                      disabled={!(permissions.CREATE_STATEMENTBALANCE || permissions.EDIT_STATEMENTBALANCE)}
                      active
                    />
                    <Button
                      onClick={() =>
                        exportStatementBalanceToPdf(statementBalance.id, statementBalance.statementBalanceNumber)
                      }
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-download" />}
                      htmlElementProps={{ title: lang.download }}
                      active
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statementBalance.id.toString(),
                        action: StatementBalanceAction.SEND_EMAIL,
                        router: pageRouter,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-envelope" />}
                      htmlElementProps={{ title: lang.sendEmail }}
                      disabled={
                        !(
                          permissions.CREATE_STATEMENTBALANCE ||
                          permissions.EDIT_STATEMENTBALANCE ||
                          permissions.MODIFY_STATEMENTBALANCE_EMAIL
                        )
                      }
                      active
                    />
                    <Button
                      onClick={pageRouter.updateParams}
                      data={{
                        id: statementBalance.id.toString(),
                        action: StatementBalanceAction.DELETE,
                      }}
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-trash" />}
                      htmlElementProps={{ title: lang.delete }}
                      disabled={!permissions.DELETE_STATEMENTBALANCE}
                      active
                    />
                  </Box>
                </td>
              </tr>
            );
          }}
        />
      </tbody>
    </table>
  );
}
